import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Clock, XCircle, ChevronDown, ChevronRight, Wrench, FileCode, Download, ListChecks } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import api, { type ProjectIssue } from '../api';
import { buildIssueReferenceExport } from '../lib/issue-reference-export.js';

interface Props {
  projectId: string;
  onFixRequest?: (prompt: string) => void;
}

const PRIORITY_CONFIG: Record<string, { color: string; label: string; sortKey: number }> = {
  P0: { color: 'var(--red)', label: 'P0 Critical', sortKey: 0 },
  P1: { color: 'var(--orange)', label: 'P1 High', sortKey: 1 },
  P2: { color: 'var(--yellow, #eab308)', label: 'P2 Medium', sortKey: 2 },
  P3: { color: 'var(--text-dim)', label: 'P3 Low', sortKey: 3 },
};

const PRIORITY_LABELS: Record<string, string> = {
  P0: 'Critical', P1: 'High', P2: 'Medium', P3: 'Low',
};

const PRIORITY_EMOJI: Record<string, string> = {
  P0: '\uD83D\uDD34', P1: '\uD83D\uDFE0', P2: '\uD83D\uDFE1', P3: '\uD83D\uDD35',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  open: <AlertTriangle size={12} style={{ color: 'var(--orange)' }} />,
  in_progress: <Clock size={12} style={{ color: 'var(--blue, #3b82f6)' }} />,
  resolved: <CheckCircle size={12} style={{ color: 'var(--green)' }} />,
  dismissed: <XCircle size={12} style={{ color: 'var(--text-dim)' }} />,
};

const EFFORT_COLORS: Record<string, string> = {
  XS: 'var(--green)', S: 'var(--green)', M: 'var(--yellow, #eab308)',
  L: 'var(--orange)', XL: 'var(--red)',
};

type SourceFilter = 'all' | 'review' | 'manual';

const MAX_BULK_FIX = 20;

/** Sort issues by priority, then file_path, then title */
function sortByPriority(issues: ProjectIssue[]): ProjectIssue[] {
  return [...issues].sort((a: ProjectIssue, b: ProjectIssue) => {
    const pa = PRIORITY_CONFIG[a.priority]?.sortKey ?? 2;
    const pb = PRIORITY_CONFIG[b.priority]?.sortKey ?? 2;
    if (pa !== pb) return pa - pb;
    const fa = a.file_path ?? '';
    const fb = b.file_path ?? '';
    if (fa !== fb) return fa.localeCompare(fb);
    return a.title.localeCompare(b.title);
  });
}

/** Build numbered task list for bulk fix chat prompt */
function buildBulkFixPrompt(issues: ProjectIssue[]): string {
  const sorted = sortByPriority(issues);
  const lines = sorted.map((issue: ProjectIssue, i: number) => {
    const parts = [`${i + 1}. [${issue.priority}] ${PRIORITY_LABELS[issue.priority] || 'Medium'}: ${issue.title}`];
    if (issue.file_path) parts.push(`   File: ${issue.file_path}`);
    if (issue.remediation) parts.push(`   Remediation: ${issue.remediation}`);
    parts.push(`   Finding ID: ${issue.id}`);
    return parts.join('\n');
  });
  return `Fix the following ${sorted.length} findings in priority order:\n\n${lines.join('\n\n')}\n\nWork through them in order. Mark each as resolved as you complete it.`;
}

/** Build markdown report for export */
function buildFindingsMarkdown(issues: ProjectIssue[], projectName: string, activeFilter: string): string {
  const sorted = sortByPriority(issues);
  const counts: Record<string, number> = { P0: 0, P1: 0, P2: 0, P3: 0 };
  for (const i of sorted) counts[i.priority] = (counts[i.priority] ?? 0) + 1;

  const lines: string[] = [
    '# OCNexus Findings Report',
    `**Project:** ${projectName}`,
    `**Generated:** ${new Date().toISOString()}`,
    `**Total findings:** ${sorted.length} (Critical: ${counts.P0}, High: ${counts.P1}, Medium: ${counts.P2}, Low: ${counts.P3})`,
    `**Filter applied:** ${activeFilter}`,
    '',
    '---',
    '',
  ];

  let num = 0;
  for (const priority of ['P0', 'P1', 'P2', 'P3']) {
    const group = sorted.filter((i: ProjectIssue) => i.priority === priority);
    if (group.length === 0) continue;
    const emoji = PRIORITY_EMOJI[priority] ?? '';
    lines.push(`## ${emoji} ${PRIORITY_LABELS[priority] ?? priority} (${group.length})`);
    lines.push('');
    for (const issue of group) {
      num++;
      lines.push(`### ${num}. ${issue.title}`);
      if (issue.file_path) lines.push(`- **File:** \`${issue.file_path}\``);
      if (issue.review_engine) lines.push(`- **Engine:** ${issue.review_engine === 'manus' ? 'MANUS' : 'QUICK'}`);
      if (issue.tag) lines.push(`- **Category:** ${issue.tag}`);
      if (issue.effort_estimate) lines.push(`- **Effort:** ${issue.effort_estimate}`);
      if (issue.remediation) lines.push(`- **Remediation:** ${issue.remediation}`);
      if (issue.description) lines.push(`- **Description:** ${issue.description}`);
      lines.push('');
    }
    lines.push('---');
    lines.push('');
  }

  return lines.join('\n');
}

export function IssuesPanel({ projectId, onFixRequest }: Props) {
  const [issues, setIssues] = useState<ProjectIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('open');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exportOpen, setExportOpen] = useState(false);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    try {
      const result = await (api as any).issues.list(projectId, filter === 'all' ? undefined : filter);
      setIssues(result);
    } catch {
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, filter]);

  useEffect(() => { void loadIssues(); }, [loadIssues]);

  // Clear selection when filter changes
  useEffect(() => { setSelected(new Set()); }, [filter, sourceFilter]);

  const updateStatus = async (issueId: string, status: string) => {
    try {
      await (api as any).issues.updateStatus(issueId, status);
      void loadIssues();
    } catch { /* ignore */ }
  };

  const handleFix = (issue: ProjectIssue) => {
    if (!onFixRequest) return;
    const parts = [`Fix the ${issue.priority} issue: ${issue.title}.`];
    if (issue.file_path) parts.push(`File: ${issue.file_path}`);
    if (issue.remediation) parts.push(issue.remediation);
    onFixRequest(parts.join(' '));
  };

  const handleBulkFix = () => {
    if (!onFixRequest || selected.size === 0) return;
    const selectedIssues = filtered.filter((i: ProjectIssue) => selected.has(i.id));
    const capped = sortByPriority(selectedIssues).slice(0, MAX_BULK_FIX);
    onFixRequest(buildBulkFixPrompt(capped));
    setSelected(new Set());
  };

  const handleFixAll = () => {
    if (!onFixRequest) return;
    const capped = sortByPriority(filtered).slice(0, MAX_BULK_FIX);
    onFixRequest(buildBulkFixPrompt(capped));
    setSelected(new Set());
  };

  const toggleSelect = (id: string) => {
    setSelected((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((i: ProjectIssue) => i.id)));
    }
  };

  // Apply source filter
  const filtered = sourceFilter === 'all'
    ? issues
    : issues.filter((i: ProjectIssue) => {
        if (sourceFilter === 'review') return i.source === 'review';
        return i.source === 'manual';
      });

  const grouped = filtered.reduce<Record<string, ProjectIssue[]>>((acc, issue) => {
    const p = issue.priority || 'P2';
    if (!acc[p]) acc[p] = [];
    acc[p].push(issue);
    return acc;
  }, {});

  const openCount = issues.filter((i: ProjectIssue) => i.status === 'open').length;

  // Export helpers
  const getFilterLabel = (): string => {
    const parts: string[] = [];
    if (filter !== 'all') parts.push(filter);
    if (sourceFilter !== 'all') parts.push(sourceFilter === 'review' ? 'From Reviews' : 'Manual');
    return parts.length > 0 ? parts.join(', ') : 'All';
  };

  const projectName = projectId; // project ID as fallback

  const handleExportMarkdown = () => {
    const md = buildFindingsMarkdown(filtered, projectName, getFilterLabel());
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, `findings-${projectId}.md`);
    setExportOpen(false);
  };

  const handleExportIssueReferences = () => {
    const references = buildIssueReferenceExport(filtered, {
      projectId, statusFilter: filter, sourceFilter, loadedRowCount: issues.length,
    });
    const blob = new Blob([JSON.stringify(references, null, 2) + '\n'], { type: 'application/json;charset=utf-8' });
    saveAs(blob, `issue-references-${projectId}.json`);
    setExportOpen(false);
  };

  const handleExportPdf = () => {
    const md = buildFindingsMarkdown(filtered, projectName, getFilterLabel());
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('OCNexus Findings Report', 14, 20);
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toISOString()}`, 14, 27);
    doc.text(`Total: ${filtered.length} findings | Filter: ${getFilterLabel()}`, 14, 32);
    doc.setFontSize(10);
    // Strip markdown formatting for plain-text PDF
    const plain = md
      .replace(/^#{1,3}\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1');
    const lines = doc.splitTextToSize(plain, 180);
    let y = 40;
    for (const line of lines) {
      if (y > 280) { doc.addPage(); y = 14; }
      doc.text(line, 14, y);
      y += 5;
    }
    // Footer on each page
    const pages = doc.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.text(`Page ${p}/${pages} — Generated by OCNexus — portal.ocnexus.dev`, 14, 290);
    }
    doc.save(`findings-${projectId}.pdf`);
    setExportOpen(false);
  };

  const handleExportGDocs = () => {
    const md = buildFindingsMarkdown(filtered, projectName, getFilterLabel());
    navigator.clipboard.writeText(md).then(() => {
      window.open('https://docs.google.com/document/create?title=' + encodeURIComponent(`OCNexus Findings - ${projectId}`), '_blank');
    }).catch(() => {
      // Fallback: offer plain text download instead of silently failing
      const blob = new Blob([md], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `findings-${projectId}.txt`);
    });
    setExportOpen(false);
  };

  return (
    <div style={{ marginTop: 8 }}>
      {/* Status filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {['open', 'in_progress', 'resolved', 'dismissed', 'all'].map((s) => (
          <button
            key={s}
            className={`btn btn-sm ${filter === s ? '' : 'btn-ghost'}`}
            onClick={() => setFilter(s)}
            style={{ fontSize: 'var(--text-xs)', padding: '3px 10px', textTransform: 'capitalize' }}
          >
            {s === 'in_progress' ? 'In Progress' : s === 'all' ? 'All' : s}
            {s === 'open' && openCount > 0 && (
              <span style={{
                marginLeft: 4, padding: '0 5px', borderRadius: 8,
                background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700,
              }}>
                {openCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Source filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['all', 'review', 'manual'] as SourceFilter[]).map((s) => (
          <button
            key={s}
            className={`btn btn-sm ${sourceFilter === s ? '' : 'btn-ghost'}`}
            onClick={() => setSourceFilter(s)}
            style={{ fontSize: 'var(--text-xs)', padding: '3px 10px', textTransform: 'capitalize' }}
          >
            {s === 'all' ? 'All Sources' : s === 'review' ? 'From Reviews' : 'Manual'}
          </button>
        ))}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Bulk action buttons */}
        {onFixRequest && filtered.length > 0 && (
          <>
            {selected.size > 0 && (
              <button
                className="btn btn-sm btn-primary"
                onClick={handleBulkFix}
                style={{ fontSize: 'var(--text-xs)', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Wrench size={10} />
                Fix Selected ({Math.min(selected.size, MAX_BULK_FIX)})
                {selected.size > MAX_BULK_FIX && (
                  <span style={{ fontSize: 8, opacity: 0.7 }}>(max {MAX_BULK_FIX})</span>
                )}
              </button>
            )}
            <button
              className="btn btn-sm"
              onClick={handleFixAll}
              style={{ fontSize: 'var(--text-xs)', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <ListChecks size={10} />
              Fix All{filtered.length > MAX_BULK_FIX ? ` (top ${MAX_BULK_FIX})` : ` (${filtered.length})`}
            </button>
          </>
        )}

        {/* Export dropdown */}
        {filtered.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-sm"
              onClick={() => setExportOpen(!exportOpen)}
              style={{ fontSize: 'var(--text-xs)', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Download size={10} /> Export
            </button>
            {exportOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4,
                background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', overflow: 'hidden', zIndex: 100,
                minWidth: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              }}>
                {[
                  { label: 'PDF (.pdf)', action: handleExportPdf },
                  { label: 'Markdown (.md)', action: handleExportMarkdown },
                  { label: 'Issue references (.json)', action: handleExportIssueReferences },
                  { label: 'Google Docs', action: handleExportGDocs },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 14px', background: 'none', border: 'none',
                      color: 'var(--text)', cursor: 'pointer', fontSize: 'var(--text-sm)',
                      borderBottom: '1px solid var(--border)',
                    }}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.background = 'var(--surface3)'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'none'; }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dim)', fontSize: 'var(--text-sm)' }}>
          Loading issues...
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 20, textAlign: 'center' }}>
          <CheckCircle size={20} style={{ color: 'var(--green)', margin: '0 auto 8px' }} />
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            No {filter === 'all' ? '' : filter} issues found
          </div>
        </div>
      ) : (
        <>
          {/* Select all toggle */}
          {onFixRequest && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingLeft: 2 }}>
              <input
                type="checkbox"
                checked={filtered.length > 0 && selected.size === filtered.length}
                onChange={toggleSelectAll}
                style={{ width: 14, height: 14, accentColor: 'var(--blue)' }}
              />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                Select all ({filtered.length})
              </span>
              {selected.size > MAX_BULK_FIX && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--orange)' }}>
                  Only top {MAX_BULK_FIX} by priority will be sent
                </span>
              )}
            </div>
          )}

          {['P0', 'P1', 'P2', 'P3'].map((priority) => {
            const group = grouped[priority];
            if (!group || group.length === 0) return null;
            const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.P2;
            return (
              <div key={priority} style={{ marginBottom: 12 }}>
                <div style={{
                  fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: config.color, marginBottom: 6,
                }}>
                  {config.label} ({group.length})
                </div>
                {group.map((issue) => {
                  const expanded = expandedId === issue.id;
                  const isSelected = selected.has(issue.id);
                  return (
                    <div
                      key={issue.id}
                      className="card"
                      data-issue-id={issue.id}
                      data-project-id={issue.project_id}
                      data-finding-id={issue.finding_id ?? ''}
                      data-status={issue.status}
                      data-source={issue.source ?? ''}
                      data-priority={issue.priority}
                      style={{
                        marginBottom: 6, padding: 0, overflow: 'hidden',
                        borderColor: isSelected ? 'var(--blue)' : undefined,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '10px 14px', cursor: 'pointer',
                        }}
                      >
                        {/* Checkbox */}
                        {onFixRequest && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => { e.stopPropagation(); toggleSelect(issue.id); }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: 14, height: 14, accentColor: 'var(--blue)', flexShrink: 0 }}
                          />
                        )}
                        <div
                          onClick={() => setExpandedId(expanded ? null : issue.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}
                        >
                          {STATUS_ICON[issue.status] || STATUS_ICON.open}
                          <span style={{
                            flex: 1, fontSize: 'var(--text-sm)', fontWeight: 500,
                            color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {issue.title}
                          </span>
                          {/* Engine badge */}
                          {issue.review_engine && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 3,
                              background: issue.review_engine === 'manus' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(168, 85, 247, 0.15)',
                              color: issue.review_engine === 'manus' ? '#f59e0b' : '#a855f7',
                              fontWeight: 600, flexShrink: 0,
                            }}>
                              {issue.review_engine === 'manus' ? 'DEEP' : 'QUICK'}
                            </span>
                          )}
                          {/* Effort estimate */}
                          {issue.effort_estimate && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 3,
                              border: `1px solid ${EFFORT_COLORS[issue.effort_estimate] || 'var(--border)'}`,
                              color: EFFORT_COLORS[issue.effort_estimate] || 'var(--text-dim)',
                              flexShrink: 0,
                            }}>
                              {issue.effort_estimate}
                            </span>
                          )}
                          {/* Tag */}
                          {issue.tag && (
                            <span style={{
                              fontSize: 9, padding: '1px 5px', borderRadius: 3,
                              background: 'var(--surface3)', color: 'var(--text-dim)',
                              flexShrink: 0,
                            }}>
                              {issue.tag}
                            </span>
                          )}
                          {/* Priority badge */}
                          <span style={{
                            fontSize: 10, padding: '1px 6px', borderRadius: 4,
                            border: `1px solid ${config.color}`, color: config.color,
                            flexShrink: 0,
                          }}>
                            {priority}
                          </span>
                          {expanded ? <ChevronDown size={12} className="muted" /> : <ChevronRight size={12} className="muted" />}
                        </div>
                      </div>
                      {expanded && (
                        <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--border)' }}>
                          {/* File path */}
                          {issue.file_path && (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              fontSize: 'var(--text-xs)', color: 'var(--blue, #3b82f6)',
                              fontFamily: 'var(--font-mono)', margin: '8px 0 4px',
                            }}>
                              <FileCode size={10} />
                              {issue.file_path}{issue.line_number ? `:${issue.line_number}` : ''}
                            </div>
                          )}
                          <p style={{
                            fontSize: 'var(--text-xs)', color: 'var(--text-muted)',
                            lineHeight: 1.6, margin: '6px 0',
                            whiteSpace: 'pre-wrap',
                          }}>
                            {issue.description}
                          </p>
                          {/* Remediation */}
                          {issue.remediation && (
                            <div style={{
                              fontSize: 'var(--text-xs)', color: 'var(--green)',
                              padding: '6px 10px', margin: '6px 0',
                              background: 'rgba(34, 197, 94, 0.06)',
                              border: '1px solid rgba(34, 197, 94, 0.2)',
                              borderRadius: 'var(--radius-md, 4px)',
                              lineHeight: 1.5,
                            }}>
                              <strong>Fix:</strong> {issue.remediation}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                            {/* Fix with OCNexus button */}
                            {onFixRequest && (issue.status === 'open' || issue.status === 'in_progress') && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={(e) => { e.stopPropagation(); handleFix(issue); }}
                                style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                              >
                                <Wrench size={10} /> Fix with OCNexus
                              </button>
                            )}
                            {issue.status === 'open' && (
                              <button
                                className="btn btn-sm"
                                onClick={(e) => { e.stopPropagation(); void updateStatus(issue.id, 'in_progress'); }}
                                style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                              >
                                Start
                              </button>
                            )}
                            {(issue.status === 'open' || issue.status === 'in_progress') && (
                              <>
                                <button
                                  className="btn btn-sm"
                                  onClick={(e) => { e.stopPropagation(); void updateStatus(issue.id, 'resolved'); }}
                                  style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                                >
                                  Resolve
                                </button>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  onClick={(e) => { e.stopPropagation(); void updateStatus(issue.id, 'dismissed'); }}
                                  style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                                >
                                  Dismiss
                                </button>
                              </>
                            )}
                          </div>
                          <div style={{
                            fontSize: 10, color: 'var(--text-dim)', marginTop: 8,
                            fontFamily: 'var(--font-mono)',
                          }}>
                            Created {new Date(issue.created_at).toLocaleDateString()}
                            {issue.finding_id && <span> · From sync finding</span>}
                            {issue.source === 'manual' && <span> · Manual</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
